import { useState } from "preact/hooks";
import { Button, Chip, Panel } from "../components";
import type { Lang } from "../content/types";
import { downloadIcs } from "../lib/ics";
import { exportProfileJson, importProfileJson } from "../state/persist";
import { PROFILE_KEY, type SessionLength, type ThemeSetting } from "../state/profile";
import {
  pauseStreak,
  profile,
  resetProfile,
  setIdentityLangs,
  setMicEnabled,
  setNewPerDay,
  setReviewCap,
  setSessionLength,
  setSoundSetting,
  setTheme,
  setTranscribeCoach,
  setWeekTarget,
} from "../state/store";

function ToggleRow({ label, detail, value, onChange }: { label: string; detail?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div class="settings-row">
      <div class="settings-row-text">
        <p class="settings-row-label">{label}</p>
        {detail && <p class="settings-row-detail">{detail}</p>}
      </div>
      <Chip active={value} onClick={() => onChange(!value)}>
        {value ? "On" : "Off"}
      </Chip>
    </div>
  );
}

function downloadJson(filename: string, contents: string) {
  const blob = new Blob([contents], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function Settings() {
  const p = profile.value;
  const [resetStep, setResetStep] = useState<0 | 1>(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [pauseDays, setPauseDays] = useState(7);
  const [reminderTime, setReminderTime] = useState("08:00");

  function handleImportFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      const result = importProfileJson(text);
      if (!result.ok) {
        setImportError(result.error);
        return;
      }
      setImportError(null);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(result.profile));
      location.reload();
    });
    input.value = "";
  }

  function toggleLang(lang: Lang) {
    const has = p.identity.langs.includes(lang);
    const next = has ? p.identity.langs.filter((l) => l !== lang) : [...p.identity.langs, lang];
    if (next.length === 0) return; // must keep at least one language
    setIdentityLangs(next);
  }

  return (
    <div class="screen screen-settings">
      <p class="display screen-title">Settings</p>

      <Panel class="settings-block">
        <p class="label">Appearance</p>
        <div class="settings-chip-row">
          {(["system", "dark", "light"] as ThemeSetting[]).map((t) => (
            <Chip key={t} active={p.settings.theme === t} onClick={() => setTheme(t)}>
              {t === "system" ? "System" : t === "dark" ? "Dark" : "Daylight"}
            </Chip>
          ))}
        </div>
      </Panel>

      <Panel class="settings-block">
        <p class="label">Sound</p>
        <ToggleRow label="Master" value={p.settings.sound.master} onChange={(v) => setSoundSetting("master", v)} />
        <ToggleRow label="Metronome" value={p.settings.sound.metronome} onChange={(v) => setSoundSetting("metronome", v)} />
        <ToggleRow label="UI sounds" value={p.settings.sound.sfx} onChange={(v) => setSoundSetting("sfx", v)} />
        <ToggleRow
          label="Voice cues"
          detail="Counts you in and names vowels aloud, so you can drill with eyes closed."
          value={p.settings.sound.voice}
          onChange={(v) => setSoundSetting("voice", v)}
        />
      </Panel>

      <Panel class="settings-block">
        <p class="label">Microphone</p>
        <ToggleRow
          label="Local voice analysis"
          detail="Your audio is analyzed on this device and never recorded, stored, or sent anywhere."
          value={p.settings.mic}
          onChange={setMicEnabled}
        />
        <ToggleRow
          label="Transcribe Coach"
          detail="Uses your browser's built-in speech recognition, which on some platforms sends audio to the browser vendor (not to us) for processing. Off by default."
          value={p.settings.transcribeCoach}
          onChange={setTranscribeCoach}
        />
      </Panel>

      <Panel class="settings-block">
        <p class="label">Training</p>
        <div class="settings-row">
          <p class="settings-row-label">Languages</p>
          <div class="settings-chip-row">
            <Chip active={p.identity.langs.includes("en")} onClick={() => toggleLang("en")}>
              English
            </Chip>
            <Chip active={p.identity.langs.includes("sv")} onClick={() => toggleLang("sv")}>
              Svenska
            </Chip>
          </div>
        </div>
        <div class="settings-row">
          <p class="settings-row-label">Weekly target</p>
          <div class="settings-chip-row">
            {[3, 5, 7].map((n) => (
              <Chip key={n} active={p.week.target === n} onClick={() => setWeekTarget(n as 3 | 5 | 7)}>
                {n} days
              </Chip>
            ))}
          </div>
        </div>
        <div class="settings-row">
          <p class="settings-row-label">New cards per day</p>
          <div class="settings-chip-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <Chip key={n} active={p.settings.newPerDay === n} onClick={() => setNewPerDay(n)}>
                {n}
              </Chip>
            ))}
          </div>
        </div>
        <div class="settings-row">
          <p class="settings-row-label">Review cap</p>
          <div class="settings-chip-row">
            {[6, 8, 10, 12, 14, 16].map((n) => (
              <Chip key={n} active={p.settings.reviewCap === n} onClick={() => setReviewCap(n)}>
                {n}
              </Chip>
            ))}
          </div>
        </div>
        <div class="settings-row">
          <p class="settings-row-label">Session length</p>
          <div class="settings-chip-row">
            {(["short", "standard", "long"] as SessionLength[]).map((v) => (
              <Chip key={v} active={p.settings.sessionLength === v} onClick={() => setSessionLength(v)}>
                {v === "short" ? "Short (~3 min)" : v === "standard" ? "Standard (~5 min)" : "Long (~8 min)"}
              </Chip>
            ))}
          </div>
        </div>
      </Panel>

      <Panel class="settings-block">
        <p class="label">Streak</p>
        <p class="settings-row-detail">Pause training for a stretch — vacations shouldn't punish your streak.</p>
        <div class="settings-pause-row">
          <input
            class="mono settings-number-input"
            type="number"
            min={1}
            max={14}
            value={pauseDays}
            onInput={(e) => setPauseDays(Number((e.target as HTMLInputElement).value) || 1)}
          />
          <Button variant="ghost" onClick={() => pauseStreak(Math.min(14, Math.max(1, pauseDays)))}>
            Pause {pauseDays} day{pauseDays === 1 ? "" : "s"}
          </Button>
        </div>
      </Panel>

      <Panel class="settings-block">
        <p class="label">Reminders</p>
        <p class="settings-row-detail">
          We have no backend and no reliable way to push a notification — a calendar invite actually works.
        </p>
        <div class="settings-pause-row">
          <input
            class="mono settings-number-input settings-time-input"
            type="time"
            value={reminderTime}
            onInput={(e) => setReminderTime((e.target as HTMLInputElement).value)}
          />
          <Button
            variant="ghost"
            onClick={() =>
              downloadIcs({
                title: "Diction Dojo — 5 minute practice",
                description: "Your daily articulation session.",
                time: reminderTime,
                durationMinutes: 5,
              })
            }
          >
            Download calendar reminder
          </Button>
        </div>
      </Panel>

      <Panel class="settings-block">
        <p class="label">Data</p>
        <div class="settings-data-row">
          <Button variant="ghost" onClick={() => downloadJson("diction-dojo-export.json", exportProfileJson(p))}>
            Export
          </Button>
          <label class="settings-import-label">
            <input type="file" accept="application/json" class="settings-file-input" onChange={handleImportFile} />
            <span class="btn btn-ghost btn-md">Import</span>
          </label>
        </div>
        {importError && <p class="settings-error">{importError}</p>}

        {resetStep === 0 ? (
          <Button variant="ghost" onClick={() => setResetStep(1)}>
            Reset all progress
          </Button>
        ) : (
          <div class="settings-reset-confirm">
            <p class="settings-row-detail">
              This erases everything — export first if you want a backup. This can't be undone.
            </p>
            <div class="settings-data-row">
              <Button variant="ghost" onClick={() => setResetStep(0)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  resetProfile();
                  setResetStep(0);
                }}
              >
                Yes, erase everything
              </Button>
            </div>
          </div>
        )}
      </Panel>

      <Panel class="settings-block">
        <p class="label">About</p>
        <p class="settings-row-detail">
          This app makes zero network requests after load. No accounts, no servers, no analytics, no third-party
          fonts — enforced by a strict Content-Security-Policy, not just a promise.
        </p>
      </Panel>
    </div>
  );
}
